export const getPathWithoutQuery = (url) => (url ? String(url).split('?')[0] : '');

export const normalizeHistoryImageKey = (url) => getPathWithoutQuery(url).trim().toLowerCase();

export const getGroupKey = (group) => `${group.blob_url}-${group.timestamp}`;

export const getGroupImageUrls = (group) => {
  const sourceUrls = (
    Array.isArray(group?.blob_urls) && group.blob_urls.length > 0
      ? group.blob_urls
      : [group?.blob_url]
  );
  const uniqueUrls = [];
  const seenKeys = new Set();
  sourceUrls.forEach((rawUrl) => {
    if (typeof rawUrl !== 'string') return;
    const normalizedUrl = rawUrl.trim();
    if (!normalizedUrl) return;
    const imageKey = normalizeHistoryImageKey(normalizedUrl) || normalizedUrl.toLowerCase();
    if (seenKeys.has(imageKey)) return;
    seenKeys.add(imageKey);
    uniqueUrls.push(normalizedUrl);
  });
  return uniqueUrls;
};

export const canMergeIntoGroup = (group, item) => {
  if (!group || !item) return false;
  if (group.user_id !== item.user_id) return false;

  const sameFirstImage = (
    !!group.blob_url
    && !!item.blob_url
    && getPathWithoutQuery(group.blob_url) === getPathWithoutQuery(item.blob_url)
  );
  const sameFirstImagePlural = (
    Array.isArray(group.blob_urls)
    && Array.isArray(item.blob_urls)
    && group.blob_urls.length > 0
    && item.blob_urls.length > 0
    && getPathWithoutQuery(group.blob_urls[0]) === getPathWithoutQuery(item.blob_urls[0])
  );
  if (!(sameFirstImage || sameFirstImagePlural)) return false;

  const ts1 = new Date(group.timestamp).getTime();
  const ts2 = new Date(item.timestamp).getTime();
  if (Number.isFinite(ts1) && Number.isFinite(ts2)) {
    if (Math.abs(ts1 - ts2) >= 10 * 60 * 1000) return false;
  }
  return true;
};

const pickPreferred = (groupValue, itemValue, invalidValues = ['N/A']) => {
  if (groupValue === undefined || groupValue === null || invalidValues.includes(groupValue)) {
    return itemValue;
  }
  return groupValue;
};

export const groupHistoryItems = (rows) => {
  const groups = [];
  rows.forEach((item) => {
    const previousGroup = groups.length > 0 ? groups[groups.length - 1] : null;
    if (previousGroup && canMergeIntoGroup(previousGroup, item)) {
      previousGroup.items.push(item);
      previousGroup.resize_mode = pickPreferred(previousGroup.resize_mode, item.resize_mode);
      previousGroup.compression_mode = pickPreferred(previousGroup.compression_mode, item.compression_mode);
      previousGroup.analysis_source = pickPreferred(previousGroup.analysis_source, item.analysis_source, ['']);
      previousGroup.cache_hit = pickPreferred(previousGroup.cache_hit, item.cache_hit, []);
      previousGroup.use_ebay_rag = pickPreferred(previousGroup.use_ebay_rag, item.use_ebay_rag, []);
      previousGroup.ebay_rag_result_count = pickPreferred(previousGroup.ebay_rag_result_count, item.ebay_rag_result_count, []);
      previousGroup.use_notes_rag = pickPreferred(previousGroup.use_notes_rag, item.use_notes_rag, []);
      previousGroup.assume_hidden_items = pickPreferred(previousGroup.assume_hidden_items, item.assume_hidden_items, []);
      previousGroup.notes_rag_example_count = pickPreferred(previousGroup.notes_rag_example_count, item.notes_rag_example_count, []);
      previousGroup.usage = pickPreferred(previousGroup.usage, item.usage, [null]);
      previousGroup.input_tokens_consumed = pickPreferred(previousGroup.input_tokens_consumed, item.input_tokens_consumed, []);
      previousGroup.output_tokens_consumed = pickPreferred(previousGroup.output_tokens_consumed, item.output_tokens_consumed, []);
      previousGroup.total_tokens_consumed = pickPreferred(previousGroup.total_tokens_consumed, item.total_tokens_consumed, []);
      return;
    }
    groups.push({
      blob_url: item.blob_url,
      blob_urls: item.blob_urls,
      timestamp: item.timestamp,
      username: item.username,
      user_id: item.user_id,
      user_deleted: item.user_deleted,
      model_name: item.model_name,
      resize_mode: item.resize_mode,
      compression_mode: item.compression_mode,
      analysis_duration_ms: item.analysis_duration_ms,
      analysis_cost: item.analysis_cost,
      analysis_cost_display: item.analysis_cost_display,
      analysis_source: item.analysis_source,
      cache_hit: item.cache_hit,
      use_ebay_rag: item.use_ebay_rag,
      ebay_rag_result_count: item.ebay_rag_result_count,
      use_notes_rag: item.use_notes_rag,
      assume_hidden_items: item.assume_hidden_items,
      notes_rag_example_count: item.notes_rag_example_count,
      usage: item.usage,
      input_tokens_consumed: item.input_tokens_consumed,
      output_tokens_consumed: item.output_tokens_consumed,
      total_tokens_consumed: item.total_tokens_consumed,
      items: [item],
    });
  });
  return groups;
};
